import {
  Box,
  BoxProps,
  chakra,
  Icon,
  Menu,
  MenuContentProps,
  MenuItemProps,
  MenuRootProps,
  MenuTriggerProps,
  Portal,
  useMenuContext,
} from '@chakra-ui/react';
import React, {
  FC,
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useId,
} from 'react';
import { MTooltip } from './MTooltip';

/*
 * Built on Chakra Menu. Stripped to the minimum supported shape so Chakra's
 * built-in click handling, keyboard navigation, focus return, and Escape
 * behavior all just work — we don't intercept any of it.
 *
 * The MenuButton is its own button; `renderTrigger` content (text, icon)
 * goes inside as visual children. The button picks up V2 focus styling via
 * `_focusVisible: { boxShadow: 'focus' }` (the V2 design-system halo). When
 * `renderTrigger` returns a real button instead, pass
 * `menuButtonProps={{ asChild: true }}` (see the prop's docs).
 *
 * v3's `Menu.Root` no longer supports the v2 render-prop pattern
 * (`<Menu.Root>{({isOpen, onClose}) => ...}</Menu.Root>` — its `children` is
 * a plain `ReactNode`, not a function). `isOpen`/`onClose` for `renderTrigger`
 * and the function-children form are sourced instead via `useMenuContext()`
 * from a child component rendered inside `Menu.Root`.
 */

type MenuPlacement = NonNullable<MenuRootProps['positioning']>['placement'];

interface MDropdownActionsProps {
  children:
    | React.ReactNode
    | ((args: { isOpen: boolean; onClose: () => void }) => React.ReactNode);
  renderTrigger: (isOpen?: boolean, onClose?: () => void) => React.ReactElement;
  popOverContentProps?: MenuContentProps;
  isDisabled?: boolean;
  boxProps?: BoxProps & { 'data-testid'?: string };
  placement?: MenuPlacement;
  popoverProps?: Omit<MenuRootProps, 'children'>;
  /**
   * Forwarded onto the MenuButton so callers can provide a meaningful
   * `aria-label` and `data-testid` (the actual interactive element is the
   * MenuButton, not the icon `renderTrigger` returns).
   *
   * Pass `asChild` when `renderTrigger` returns its own button (`MButton`,
   * `MCustomIconButton`): the menu attaches to that button instead of
   * wrapping it — a `<button>` inside a `<button>` is invalid HTML — and the
   * button keeps its own variant styling and accessible name.
   */
  menuButtonProps?: Omit<MenuTriggerProps, 'children'> & {
    'data-testid'?: string;
  };
  /**
   * Item highlighted when the menu opens (pass the ref of an
   * `MDropdownActionItem`). v2's Menu took this as `initialFocusRef`; v3's
   * `Menu.Root` has no equivalent prop, so it's replicated here by hand.
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

interface MDropdownActionsContentProps extends Pick<
  MDropdownActionsProps,
  | 'renderTrigger'
  | 'children'
  | 'isDisabled'
  | 'menuButtonProps'
  | 'popOverContentProps'
  | 'initialFocusRef'
> {}

/**
 * Highlights `initialFocusRef`'s item when the menu opens. Ark keeps DOM
 * focus on the menu content and marks the active item via
 * `data-highlighted`/`aria-activedescendant`, so `.focus()` on an item is a
 * no-op; the item is highlighted by its `value` (rendered as `data-value`)
 * instead. Rendered inside `Menu.Content` so it mounts in the same commit as
 * the (lazily mounted) items — at the root, the ref is still null on open.
 */
const MenuInitialHighlight = ({
  initialFocusRef,
}: {
  initialFocusRef: React.RefObject<HTMLElement>;
}) => {
  const { open, setHighlightedValue } = useMenuContext();

  useEffect(() => {
    const initialValue = initialFocusRef.current?.dataset.value;
    if (open && initialValue) {
      setHighlightedValue(initialValue);
    }
    // `setHighlightedValue` is recreated on every render; depending on it
    // would snap the highlight back each time arrow keys move it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFocusRef]);

  return null;
};

const MDropdownActionsContent: FC<MDropdownActionsContentProps> = ({
  renderTrigger,
  children,
  isDisabled,
  menuButtonProps,
  popOverContentProps,
  initialFocusRef,
}) => {
  const { open, setOpen } = useMenuContext();
  const onClose = useCallback(() => setOpen(false), [setOpen]);

  // With `asChild` the caller's button is the trigger: `unstyled` drops the
  // menu recipe's trigger slot (its `color`/`_hover` would override the
  // button's variant), and the button's own text or aria-label names it.
  const isAsChild = !!menuButtonProps?.asChild;
  const wrapperStyles = isAsChild
    ? undefined
    : {
        bg: 'transparent',
        border: 'none',
        p: 0,
        m: 0,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        lineHeight: 1,
        borderRadius: 'sm',
        _focusVisible: {
          outline: 'none',
          boxShadow: 'focus',
        },
      };

  return (
    <>
      <Menu.Trigger
        type="button"
        unstyled={isAsChild}
        aria-label={isAsChild ? undefined : 'More actions'}
        {...menuButtonProps}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        onClickCapture={(e) => {
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onKeyDownCapture={(e) => {
          if (
            isDisabled &&
            (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        {...wrapperStyles}
      >
        {renderTrigger(open, onClose)}
      </Menu.Trigger>
      <Portal>
        <Portal>
          <Menu.Positioner>
            <Menu.Content {...popOverContentProps}>
              {initialFocusRef && (
                <MenuInitialHighlight initialFocusRef={initialFocusRef} />
              )}
              {typeof children === 'function'
                ? children({ isOpen: open, onClose })
                : children}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Portal>
    </>
  );
};

const MDropdownActions: FC<MDropdownActionsProps> = ({
  renderTrigger,
  children,
  popOverContentProps,
  boxProps,
  popoverProps,
  placement = 'bottom-end',
  isDisabled,
  menuButtonProps,
  initialFocusRef,
}: MDropdownActionsProps) => (
  <Box position="relative" onClick={(e) => e.stopPropagation()} {...boxProps}>
    <Menu.Root
      lazyMount
      unmountOnExit
      {...popoverProps}
      positioning={{
        placement,
        ...popoverProps?.positioning,
      }}
    >
      <MDropdownActionsContent
        renderTrigger={renderTrigger}
        isDisabled={isDisabled}
        menuButtonProps={menuButtonProps}
        popOverContentProps={popOverContentProps}
        initialFocusRef={initialFocusRef}
      >
        {children}
      </MDropdownActionsContent>
    </Menu.Root>
  </Box>
);

export const MIconAction = ({
  icon,
  ...rest
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  'aria-label'?: string;
}) => <Icon as={icon} fontSize="lg" {...rest} />;

/*
 * `colorScheme` is omitted (it's the CSS `color-scheme` property in v3). This
 * item used to take it as an alias for text color; pass `color` instead.
 */
interface MDropdownActionItemProps extends Omit<
  MenuItemProps,
  'colorScheme' | 'value'
> {
  children?: React.ReactNode;
  id?: string;
  isDisabled?: boolean;
  tooltipMsg?: string;
  hasTopDivider?: boolean;
  hasBottomDivider?: boolean;
  /** Optional — v3's `Menu.Item` requires a unique `value` per item; falls
   * back to `id` or a generated one when omitted. */
  value?: string;
}

export const MDropdownActionItem = forwardRef<
  HTMLDivElement,
  MDropdownActionItemProps
>(
  (
    {
      children,
      id,
      isDisabled,
      tooltipMsg,
      hasTopDivider,
      hasBottomDivider,
      value,
      ...rest
    },
    ref,
  ) => {
    // `value` is a required, unique-per-item identifier in v3 (no v2
    // equivalent) — Ark derives each item's DOM id and typeahead/highlight
    // tracking from it, so it can't be a shared literal across items.
    // Falls back to a stable generated id when the caller doesn't pass one.
    const generatedValue = useId();

    const item = (
      <Menu.Item
        ref={ref}
        disabled={isDisabled}
        color="fg.primary"
        data-testid={`data-table-actions-${id}`}
        {...rest}
        value={value ?? id ?? generatedValue}
      >
        {children}
      </Menu.Item>
    );

    return (
      <Fragment>
        {hasTopDivider && <Menu.Separator />}
        {tooltipMsg ? (
          <MTooltip label={tooltipMsg} placement="bottom-end">
            {item}
          </MTooltip>
        ) : (
          item
        )}
        {hasBottomDivider && <Menu.Separator />}
      </Fragment>
    );
  },
);

export default chakra(MDropdownActions);
